<script setup>
import { router } from '@inertiajs/vue3';
import ManageMediasAction from '@js/components/actions/ManageMediasAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { configuration, tenant } = defineProps({
    configuration: {
        required: true,
        type: Object,
    },
    tenant: {
        required: true,
        type: Object,
    },
});

const transactionEmailApis = [
    { value: 'mailjet', label: 'Mailjet' },
    { value: 'amazonSes', label: 'Amazon SES' },
];

const { route } = useRoute();

const form = reactive({
    transactionEmailApi:
        transactionEmailApis.find(
            (api) => api.value === configuration?.data.transactionEmailApi,
        ) ?? null,
    monthlyEmailLimit: configuration.data.monthlyEmailLimit,
});

const isSubmitting = ref(false);

const validationSchema = object({
    transactionEmailApi: object().required(
        "L'API d'envoi des emails transactionnels est requise",
    ),
    monthlyEmailLimit: string().nullable(),
});

function saveAction() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postEmailingAutomationConfiguration',
                {
                    tenant: tenant.id,
                },
            ),
            {
                transactionEmailApi: form.transactionEmailApi.value,
                monthlyEmailLimit: form.monthlyEmailLimit,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                    tab: 'configuration',
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="saveAction"
    >
        <FormColumn>
            <FormContent>
                <ManageMediasAction
                    :path="
                        'tenant/' + tenant.id + '/emailing-automations/media'
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Gérer les médias de l'emailing automatisé
                        </ActionButton>
                    </template>
                </ManageMediasAction>
            </FormContent>

            <FormContent>
                <ListboxField
                    v-model="form.transactionEmailApi"
                    field-id="transactionEmailApi"
                    field-name="transactionEmailApi"
                    label="API d'envoi des emails transactionnels"
                    :options="transactionEmailApis"
                    required
                >
                    <template #buttonContent>
                        {{ form.transactionEmailApi.label }}
                    </template>

                    <template #optionContent="{ option }">
                        {{ option.label }}
                    </template>
                </ListboxField>
            </FormContent>

            <FormContent>
                <InputField
                    v-model="form.monthlyEmailLimit"
                    label="Limite mensuelle d'emails"
                    field-name="monthlyEmailLimit"
                    field-id="monthlyEmailLimit"
                    type="number"
                    min="0"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
